import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfirmDialog, TableKit } from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/tablekit/TableKit.css";
import {
  meetingsApi,
  type MeetingFull,
  type MeetingListItem,
} from "../../../api/meetingsApi";
import { toClientApiError } from "../../../api/httpClient";
import { ClearOutlinedIcon, OpenInNewOutlinedIcon } from "../../../ui/appIcons";
import { QuietButton } from "../../../ui/QuietButton";
import { TableRowActions } from "../../../ui/TableRowActions";
import { formatMeetingDateTime, MEETING_STATUS_LABELS } from "../constants";
import { TeamMeetingCard } from "./TeamMeetingCard";

const STATUS_FILTERS = [
  { value: "all", label: "הכל" },
  { value: "completed", label: "הושלמו" },
  { value: "scheduled", label: "מתוכננות" },
  { value: "preparation", label: "בהכנה" },
  { value: "cancelled", label: "בוטלו" },
];

type Props = {
  canManage: boolean;
  accent: string;
  onNewMeeting: () => void;
  onCreateFromPrevious: (id: string) => void;
  onResumeMeeting: (meeting: MeetingFull) => void;
  autoOpenId?: string | null;
  onSnack?: (message: string, type?: "success" | "error") => void;
};

export function ArchivePanel({
  canManage,
  accent,
  onNewMeeting,
  onCreateFromPrevious,
  onResumeMeeting,
  autoOpenId,
  onSnack,
}: Props) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [rows, setRows] = useState<MeetingListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailData, setDetailData] = useState<MeetingFull | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MeetingListItem | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const autoOpenedRef = useRef<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await meetingsApi.list({
        search: appliedSearch || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        page: 1,
        pageSize: 100,
      });
      setRows(res.items || []);
    } catch (err) {
      setRows([]);
      onSnack?.(toClientApiError(err).message || "שגיאה בטעינת ישיבות", "error");
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, statusFilter, onSnack]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openDetailById = useCallback(
    async (id: string) => {
      if (!id) return true;
      try {
        const item = await meetingsApi.getById(id);
        setDetailData(item);
        setDetailId(id);
        setCardOpen(true);
      } catch (err) {
        onSnack?.(toClientApiError(err).message || "שגיאה בטעינת ישיבה", "error");
      }
      return true;
    },
    [onSnack],
  );

  useEffect(() => {
    if (!autoOpenId) {
      autoOpenedRef.current = null;
      return;
    }
    const key = String(autoOpenId);
    if (autoOpenedRef.current === key) return;
    autoOpenedRef.current = key;
    void openDetailById(autoOpenId);
  }, [autoOpenId, openDetailById]);

  const columns = useMemo(
    () => [
      { key: "title", title: "כותרת", minWidth: 180, sortable: true, filterable: true },
      {
        key: "scheduledAtLabel",
        title: "תאריך",
        minWidth: 160,
        sortable: true,
        filterable: true,
      },
      {
        key: "statusLabel",
        title: "סטטוס",
        minWidth: 100,
        sortable: true,
        filterable: true,
      },
      { key: "agendaCount", title: "נושאים", minWidth: 80, sortable: true },
      { key: "participantsCount", title: "משתתפים", minWidth: 90, sortable: true },
      {
        key: "actions",
        title: "פעולות",
        minWidth: canManage ? 120 : 72,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const status = String(row.status ?? "");
          const canCancel =
            canManage && (status === "preparation" || status === "scheduled");
          return (
            <TableRowActions>
              <QuietButton
                title="פתח"
                tone="accent"
                icon={<OpenInNewOutlinedIcon />}
                onClick={() => {
                  void openDetailById(String(row.id ?? ""));
                }}
              />
              {canCancel ? (
                <QuietButton
                  title="ביטול"
                  tone="danger"
                  icon={<ClearOutlinedIcon />}
                  onClick={() => {
                    const id = String(row.id ?? "");
                    const match = rows.find((entry) => entry.id === id);
                    if (match) setCancelTarget(match);
                  }}
                />
              ) : null}
            </TableRowActions>
          );
        },
      },
    ],
    [canManage, openDetailById, rows],
  );

  const tableRows = rows.map((row) => ({
    ...row,
    scheduledAtLabel: formatMeetingDateTime(row.scheduledAt),
    statusLabel: MEETING_STATUS_LABELS[row.status] || row.status,
  }));

  const closeCard = () => {
    setCardOpen(false);
    setDetailId(null);
    setDetailData(null);
  };

  const handleOpenRow = useCallback(
    (row: Record<string, unknown>) => {
      const id = String(row.id ?? "");
      if (!id) return true;
      void openDetailById(id);
      return true;
    },
    [openDetailById],
  );

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    const cancelledId = cancelTarget.id;
    setCancelBusy(true);
    try {
      await meetingsApi.update(cancelledId, { status: "cancelled" });
      setCancelTarget(null);
      if (detailId === cancelledId) {
        closeCard();
      }
      await reload();
    } catch (err) {
      onSnack?.(toClientApiError(err).message || "שגיאה בביטול ישיבה", "error");
      setCancelTarget(null);
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <>
      <div className="tm-panel">
        <div className="tm-panel__head">
          <h3 className="tm-panel__title">ארכיון ישיבות</h3>
          {canManage ? (
            <button
              type="button"
              className="tm-btn tm-btn--primary tm-btn--sm"
              onClick={onNewMeeting}
            >
              + ישיבה חדשה
            </button>
          ) : null}
        </div>
        <div className="tm-search-row">
          <input
            type="search"
            placeholder="חיפוש בכותרת, נושאים וסיכומים…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setAppliedSearch(search.trim());
            }}
          />
          <button
            type="button"
            className="tm-btn tm-btn--outline"
            onClick={() => setAppliedSearch(search.trim())}
          >
            חפש
          </button>
        </div>
        <div className="tm-filter-pills">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={`tm-pill${statusFilter === f.value ? " tm-pill--active" : ""}`}
              style={
                statusFilter === f.value
                  ? { background: accent, borderColor: accent }
                  : undefined
              }
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <TableKit
          columns={columns}
          data={tableRows as unknown as Array<Record<string, unknown>>}
          loading={loading}
          clientSideMode
          emptyMessage="אין ישיבות"
          exportFileName="team-meetings"
          showExport
          showFilters
          showColumnPicker
          onRowClick={handleOpenRow}
          onRowDoubleClick={handleOpenRow}
          onOpenCard={handleOpenRow}
        />
      </div>
      <TeamMeetingCard
        open={cardOpen}
        meeting={detailData}
        onClose={closeCard}
        canManage={canManage}
        onRefresh={async () => {
          if (!detailId) return;
          try {
            const item = await meetingsApi.getById(detailId);
            setDetailData(item);
          } catch {
            // ignore refresh errors
          }
          void reload();
        }}
        onCreateFromPrevious={(id) => {
          closeCard();
          onCreateFromPrevious(id);
        }}
        onResumeMeeting={(m) => {
          closeCard();
          onResumeMeeting(m);
        }}
      />
      <ConfirmDialog
        open={Boolean(cancelTarget)}
        onClose={() => {
          if (!cancelBusy) setCancelTarget(null);
        }}
        title="ביטול ישיבה"
        message={
          cancelTarget
            ? `לבטל את הישיבה "${cancelTarget.title || "ישיבת צוות"}"?`
            : ""
        }
        confirmText="ביטול ישיבה"
        cancelText="חזרה"
        loading={cancelBusy}
        onConfirm={() => {
          void handleCancelConfirm();
        }}
        onCancel={() => setCancelTarget(null)}
      />
    </>
  );
}
